import { Button, Loader } from "@mantine/core";
import { IconCircleCheckFilled, IconRefresh, IconShieldCheck } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "#/components/ui/EmptyState";
import { useClient } from "#/hook/useClient";
import { useDataHealth } from "#/hook/useDataHealth";
import { CHECK_META, IMPACT_THEME } from "#/pages/datahealth/healthCatalog";
import { HealthCheckKey } from "#/types/dataHealth";
import { formatDateTime } from "#/utils/dateTimeUtils";

interface Props {
  clientId: number;
}

/**
 * TMA-admin "Data Health" tab on Client Detail: everything the admin sets for
 * this client that, when missing, breaks something on the platform. Devices
 * without a Site (scans rejected), no active user, status types the engines
 * need, geofence radius, PRV schedule. Each card says what it breaks and
 * where on this page to fix it. What the client's own users can fix lives on
 * their Data Health page, not here.
 */
const ClientDataHealthTab = ({ clientId }: Props) => {
  const { t } = useTranslation();
  const { client } = useClient();
  const { data: report, isLoading, isError, isFetching, refetch } = useDataHealth(clientId);

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader color="var(--client-color)" />
      </div>
    );
  }
  if (isError || !report) return <EmptyState title={t("common.failedToProcess")} />;

  return (
    <div className="flex flex-col gap-4 pb-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-sm text-gray-600">{t("dataHealth.account.subtitle")}</span>
          <span className="text-xs text-gray-500">
            {t("dataHealth.checkedAt", { time: formatDateTime(report.checkedAt, client) })}
            {report.openCount > 0 && (
              <>
                {" · "}
                <span className="font-medium text-gray-700">
                  {t("dataHealth.open", { count: report.openCount })}
                </span>
              </>
            )}
          </span>
        </div>
        <Button
          variant="default"
          size="xs"
          leftSection={<IconRefresh size={14} />}
          loading={isFetching}
          onClick={() => void refetch()}
        >
          {t("dataHealth.refresh")}
        </Button>
      </div>

      {report.checks.length === 0 ? (
        <div className="flex items-center gap-4 rounded-xl border border-emerald-200 bg-emerald-50 p-5">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
            <IconShieldCheck size={26} stroke={1.8} />
          </span>
          <span className="text-base font-semibold text-emerald-900">
            {t("dataHealth.account.allClear")}
          </span>
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {report.checks.map((check) => {
            const meta = CHECK_META[check.key];
            const theme = IMPACT_THEME[check.impact];
            const Icon = meta.icon;
            return (
              <li
                key={check.key}
                className={`flex flex-col gap-3 rounded-xl border border-l-4 border-gray-200 bg-white p-4 ${theme.cardBorder}`}
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${theme.iconWrap}`}
                  >
                    <Icon size={20} stroke={1.8} />
                  </span>
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-base font-semibold text-gray-900">
                        {t(`dataHealth.checks.${check.key}.title`)}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${theme.badge}`}
                      >
                        {check.count}
                      </span>
                    </span>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium text-gray-900">
                        {t("dataHealth.labels.breaks")}{" "}
                      </span>
                      {t(`dataHealth.checks.${check.key}.breaks`)}
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium text-gray-900">
                        {t("dataHealth.labels.where")}{" "}
                      </span>
                      {t(`dataHealth.checks.${check.key}.where`)}
                    </p>
                  </div>
                </div>
                {check.rows.some((row) => row.fields.length > 0) && (
                  <ul className="flex flex-wrap gap-1.5">
                    {check.rows.map((row) => (
                      <li
                        key={String(row.id)}
                        className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs text-gray-800"
                      >
                        {row.name}
                        {row.count !== null && check.key === HealthCheckKey.STATUS_NO_TYPE && (
                          <span className="text-gray-500">
                            {" · "}
                            {t("dataHealth.counts.items", { count: row.count })}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {report.passing.length > 0 && (
        <section className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-white p-4">
          <span className="text-sm font-semibold text-gray-900">
            {t("dataHealth.passing.title", { count: report.passing.length })}
          </span>
          <ul className="flex flex-wrap gap-2">
            {report.passing.map((key) => (
              <li
                key={key}
                className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs text-emerald-900"
              >
                <IconCircleCheckFilled size={14} className="text-emerald-600" />
                {t(`dataHealth.checks.${key}.title`)}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default ClientDataHealthTab;
